import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteNav from './components/SiteNav';
import BookCard from './components/BookCard';
import { API_URL } from './utils/books';
import './App.css';

function AuthorBooksPage() {
  const { authorName } = useParams();
  const decodedAuthor = decodeURIComponent(authorName || '');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage] = useState(12);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/books`)
      .then((response) => response.json())
      .then((data) => {
        const allBooks = data.books || [];
        const authorBooks = allBooks
          .filter(
            (book) =>
              (book.author || '').trim().toLowerCase() === decodedAuthor.trim().toLowerCase()
          )
          .sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)));
        setBooks(authorBooks);
        setCurrentPage(1);
        setFlippedCards(new Set());
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [decodedAuthor]);

  const toggleCardFlip = (bookId) => {
    setFlippedCards((prev) => {
      const next = new Set(prev);
      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }
      return next;
    });
  };

  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = books.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(books.length / booksPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  return (
    <div
      className="App"
      style={{
        backgroundImage: `url('/background/background.png')`,
        backgroundColor: '#f0f0f0'
      }}
    >
      <SiteNav />
      <header className="App-header">
        <div className="author-page-header">
          <Link to="/authors" className="author-back-link">
            ← All Authors
          </Link>
          <h2 className="author-page-title">{decodedAuthor}</h2>
          <p className="author-page-subtitle">
            {loading
              ? 'Loading...'
              : `${books.length} ${books.length === 1 ? 'book' : 'books'} by this author`}
          </p>
        </div>

        {error && <p className="index-status">Error: {error}</p>}

        <div className="books-section">
          {!loading && books.length > 0 && (
            <div className="results-info">
              Showing {indexOfFirstBook + 1}-{Math.min(indexOfLastBook, books.length)} of{' '}
              {books.length} books
            </div>
          )}

          <div className="books-grid">
            {loading ? (
              <p>Loading books...</p>
            ) : currentBooks.length > 0 ? (
              currentBooks.map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  isFlipped={flippedCards.has(book.id)}
                  onToggle={() => toggleCardFlip(book.id)}
                />
              ))
            ) : (
              <p>No books found for this author.</p>
            )}
          </div>

          {books.length > booksPerPage && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              <div className="pagination-numbers">
                {currentPage > 3 && totalPages > 5 && (
                  <>
                    <button
                      className="pagination-number"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                    <span className="pagination-ellipsis">...</span>
                  </>
                )}

                {getPageNumbers().map((number) => (
                  <button
                    key={number}
                    className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                    onClick={() => handlePageChange(number)}
                  >
                    {number}
                  </button>
                ))}

                {currentPage < totalPages - 2 && totalPages > 5 && (
                  <>
                    <span className="pagination-ellipsis">...</span>
                    <button
                      className="pagination-number"
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className="pagination-button"
                onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}

export default AuthorBooksPage;
